'use client';

import { useEffect, useMemo } from 'react';
import { Box } from '@terra/ui-styled-system';
import { useThemeMode } from '@terra/ui-design-system';
import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  type ColorMode,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { FindingAttackPathDto } from '@terra/shared-contracts-findings';
import { GRAPH_COLORS, useAutoLayout } from '../flow-layout';
import {
  attackPathAssetNavigation,
  buildFindingAttackPathFlow,
  type FindingAttackPathFlowEdgeData,
  type FindingAttackPathFlowNodeData,
} from './build-finding-attack-path-flow';
import { FindingAttackPathEdge } from './FindingAttackPathEdge';
import { FindingAttackPathNode } from './FindingAttackPathNode';

const nodeTypes = {
  findingAttackPathNode: FindingAttackPathNode,
};

const edgeTypes = {
  findingAttackPathEdge: FindingAttackPathEdge,
};

const FINDING_ATTACK_PATH_EDGE_MOTION_CSS = `
@keyframes finding-attack-path-edge-energy {
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: -1; }
}
@keyframes finding-attack-path-edge-dash {
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: -24; }
}
`;

type FindingAttackPathOpenAsset = {
  id: string;
  type: 'ENDPOINT' | 'WEBPAGE' | 'DISCOVERED_ASSET' | 'AI_COMPONENT';
  discoveredAssetType?: string;
};

type FindingAttackPathFlowProps = {
  attackPath: FindingAttackPathDto;
  onOpenAsset?: (asset: FindingAttackPathOpenAsset) => void;
  onOpenSignal?: (signalId: string) => void;
  onOpenFinding?: (findingId: string) => void;
};

export function FindingAttackPathFlow(props: FindingAttackPathFlowProps) {
  return (
    <ReactFlowProvider>
      <FindingAttackPathCanvas {...props} />
    </ReactFlowProvider>
  );
}

function FindingAttackPathCanvas({
  attackPath,
  onOpenAsset,
  onOpenSignal,
  onOpenFinding,
}: FindingAttackPathFlowProps) {
  const { mode: themeMode } = useThemeMode();
  const colorMode: ColorMode = themeMode === 'dark' ? 'dark' : 'light';
  const flow = useMemo(
    () => buildFindingAttackPathFlow(attackPath),
    [attackPath],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState(flow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flow.edges);

  useEffect(() => {
    setNodes(flow.nodes);
    setEdges(flow.edges);
  }, [flow.edges, flow.nodes, setEdges, setNodes]);

  const { isLayoutPending } = useAutoLayout<FindingAttackPathFlowNodeData>({
    layout: 'hierarchy',
    rankdir: 'LR',
    nodesep: 72,
    ranksep: 180,
    fitViewPadding: 0.25,
    fitViewMaxZoom: 1.1,
  });

  return (
    <Box w="full" h="full" visibility={isLayoutPending ? 'hidden' : 'visible'}>
      <style>{FINDING_ATTACK_PATH_EDGE_MOTION_CSS}</style>
      <ReactFlow<
        Node<FindingAttackPathFlowNodeData>,
        Edge<FindingAttackPathFlowEdgeData>
      >
        colorMode={colorMode}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={(_event, node) => {
          const data = node.data;

          if (data.kind === 'WEAKNESS' && data.signalId && onOpenSignal) {
            onOpenSignal(data.signalId);

            return;
          }

          if (
            data.kind === 'FINDING' &&
            data.findingId &&
            !data.isCurrent &&
            onOpenFinding
          ) {
            onOpenFinding(data.findingId);

            return;
          }

          if (data.kind !== 'ASSET' || !data.assetId || !data.assetKind) {
            return;
          }

          const navigation = attackPathAssetNavigation(data.assetKind);

          if (navigation && onOpenAsset) {
            onOpenAsset({ id: data.assetId, ...navigation });
          }
        }}
        nodesDraggable={false}
        nodesConnectable={false}
        minZoom={0.3}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        fitView
      >
        <Background
          variant={BackgroundVariant.Dots}
          color={GRAPH_COLORS.background}
          gap={12}
        />
        <Controls />
      </ReactFlow>
    </Box>
  );
}
